import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import {
  Settings,
  RefreshCw,
  Users,
  Briefcase,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

type Tab = 'feedback' | 'quality' | 'adoption' | 'services' | 'reports';
type FeedbackSource = 'end-user' | 'leadership' | 'zoom';
type Trend = 'up' | 'down' | 'flat';
type ServiceStatus = 'healthy' | 'degraded' | 'down';
type MonitorType = 'active' | 'passive';
type ReportFrequency = 'daily' | 'weekly' | 'monthly';

interface FeedbackItem {
  id: string;
  source: FeedbackSource;
  title: string;
  date: string;
}

interface QualityMetric {
  id: string;
  name: string;
  value: string;
  trend: Trend;
  change: string;
}

interface AdoptionMetric {
  id: string;
  feature: string;
  adoption: number;
  trend: Trend;
}

interface ServiceMonitor {
  id: string;
  name: string;
  type: MonitorType;
  status: ServiceStatus;
  lastCheck: string;
  description: string;
}

interface ScheduledReport {
  id: string;
  name: string;
  frequency: ReportFrequency;
  recipients: string[];
  lastSent?: string;
  nextSend: string;
  enabled: boolean;
}

const MOCK_FEEDBACK: FeedbackItem[] = [
  { id: '1', source: 'leadership', title: 'Reduce average handle time by 15%', date: 'Feb 7' },
  { id: '2', source: 'end-user', title: 'Sales team needs Spanish language routing', date: 'Feb 8' },
  { id: '3', source: 'zoom', title: 'AI Companion now available for Contact Center', date: 'Feb 9' },
  { id: '4', source: 'end-user', title: 'Request for dedicated overflow queue', date: 'Feb 5' },
  { id: '5', source: 'leadership', title: 'Audit user licenses for cost optimization', date: 'Feb 4' },
];

const MOCK_QUALITY: QualityMetric[] = [
  { id: '1', name: 'Avg Handle Time', value: '4m 32s', trend: 'down', change: '-12%' },
  { id: '2', name: 'First Call Resolution', value: '78%', trend: 'up', change: '+3%' },
  { id: '3', name: 'Customer Satisfaction', value: '4.2/5', trend: 'flat', change: '0%' },
  { id: '4', name: 'Call Abandonment', value: '8%', trend: 'down', change: '-2%' },
  { id: '5', name: 'Agent Utilization', value: '82%', trend: 'up', change: '+5%' },
];

const MOCK_ADOPTION: AdoptionMetric[] = [
  { id: '1', feature: 'Zoom Phone', adoption: 94, trend: 'flat' },
  { id: '2', feature: 'Team Chat', adoption: 87, trend: 'up' },
  { id: '3', feature: 'Contact Center', adoption: 72, trend: 'up' },
  { id: '4', feature: 'AI Companion', adoption: 23, trend: 'up' },
  { id: '5', feature: 'Whiteboard', adoption: 45, trend: 'down' },
];

const MOCK_SERVICES: ServiceMonitor[] = [
  { id: '1', name: 'Support Queue Response Time', type: 'active', status: 'healthy', lastCheck: '2m ago', description: 'Checks avg wait time < 2 min' },
  { id: '2', name: 'Sales Queue Availability', type: 'active', status: 'healthy', lastCheck: '2m ago', description: 'Verifies agents available during business hours' },
  { id: '3', name: 'Phone System Uptime', type: 'passive', status: 'healthy', lastCheck: '5m ago', description: 'Monitors Zoom Phone service status' },
  { id: '4', name: 'Call Quality Score', type: 'passive', status: 'degraded', lastCheck: '1m ago', description: 'Alerts when MOS drops below 3.5' },
  { id: '5', name: 'Contact Center API', type: 'active', status: 'healthy', lastCheck: '2m ago', description: 'Health check on CC API endpoints' },
];

const SERVICE_STATUS_CONFIG: Record<ServiceStatus, { icon: typeof CheckCircle; color: string }> = {
  healthy: { icon: CheckCircle, color: 'text-green-400' },
  degraded: { icon: AlertTriangle, color: 'text-yellow-400' },
  down: { icon: XCircle, color: 'text-red-400' },
};

const MOCK_REPORTS: ScheduledReport[] = [
  { id: '1', name: 'Weekly Quality Summary', frequency: 'weekly', recipients: ['admin@company.com', 'vp-support@company.com'], lastSent: 'Feb 5', nextSend: 'Feb 12', enabled: true },
  { id: '2', name: 'Monthly Adoption Report', frequency: 'monthly', recipients: ['leadership@company.com'], lastSent: 'Jan 31', nextSend: 'Feb 28', enabled: true },
  { id: '3', name: 'Daily Service Health', frequency: 'daily', recipients: ['admin@company.com'], lastSent: 'Today', nextSend: 'Tomorrow', enabled: true },
  { id: '4', name: 'Stakeholder Feedback Digest', frequency: 'weekly', recipients: ['admin@company.com'], lastSent: 'Feb 5', nextSend: 'Feb 12', enabled: false },
];

const SOURCE_CONFIG = {
  'end-user': { icon: Users, color: 'text-blue-400' },
  'leadership': { icon: Briefcase, color: 'text-purple-400' },
  'zoom': { icon: Zap, color: 'text-orange-400' },
};

export function MonitorPage() {
  const navigate = useNavigate();
  const { isConnected } = useConnectionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('feedback');
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [quality, setQuality] = useState<QualityMetric[]>([]);
  const [adoption, setAdoption] = useState<AdoptionMetric[]>([]);
  const [services, setServices] = useState<ServiceMonitor[]>([]);
  const [reports, setReports] = useState<ScheduledReport[]>([]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      setFeedback(MOCK_FEEDBACK);
      setQuality(MOCK_QUALITY);
      setAdoption(MOCK_ADOPTION);
      setServices(MOCK_SERVICES);
      setReports(MOCK_REPORTS);
      setIsLoading(false);
    };

    if (isConnected) {
      load();
    } else {
      setIsLoading(false);
    }
  }, [isConnected]);

  const TrendIcon = ({ trend }: { trend: Trend }) => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3 text-green-400" />;
    if (trend === 'down') return <TrendingDown className="h-3 w-3 text-red-400" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  if (!isConnected) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <Settings className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">Connect Your Zoom Account</h2>
            <p className="mt-2 text-muted-foreground">
              Connect your Zoom account to monitor feedback, quality, and adoption.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Connect in Chat
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-medium text-foreground">Monitor</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-border">
            {(['feedback', 'quality', 'adoption', 'services', 'reports'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm capitalize transition-colors border-b-2 -mb-px ${
                  tab === t
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Feedback Tab */}
              {tab === 'feedback' && (
                <div className="space-y-1">
                  {feedback.map(item => {
                    const config = SOURCE_CONFIG[item.source];
                    return (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 py-2 px-1 hover:bg-secondary/30 rounded transition-colors cursor-pointer"
                      >
                        <config.icon className={`h-4 w-4 ${config.color}`} />
                        <span className="flex-1 text-sm text-foreground">{item.title}</span>
                        <span className="text-xs text-muted-foreground">{item.date}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quality Tab */}
              {tab === 'quality' && (
                <div className="space-y-2">
                  {quality.map(metric => (
                    <div
                      key={metric.id}
                      className="flex items-center justify-between py-2 px-1"
                    >
                      <span className="text-sm text-muted-foreground">{metric.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground">{metric.value}</span>
                        <div className="flex items-center gap-1 w-16">
                          <TrendIcon trend={metric.trend} />
                          <span className={`text-xs ${
                            metric.trend === 'up' ? 'text-green-400' :
                            metric.trend === 'down' ? 'text-red-400' :
                            'text-muted-foreground'
                          }`}>
                            {metric.change}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Adoption Tab */}
              {tab === 'adoption' && (
                <div className="space-y-3">
                  {adoption.map(metric => (
                    <div key={metric.id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground">{metric.feature}</span>
                        <div className="flex items-center gap-2">
                          <TrendIcon trend={metric.trend} />
                          <span className="text-sm text-muted-foreground">{metric.adoption}%</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${metric.adoption}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Services Tab */}
              {tab === 'services' && (
                <div className="space-y-2">
                  {services.map(service => {
                    const config = SERVICE_STATUS_CONFIG[service.status];
                    const StatusIcon = config.icon;
                    return (
                      <div
                        key={service.id}
                        className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-secondary/30 transition-colors cursor-pointer"
                      >
                        <StatusIcon className={`h-4 w-4 ${config.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{service.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              service.type === 'active' ? 'bg-blue-500/20 text-blue-400' : 'bg-secondary text-muted-foreground'
                            }`}>
                              {service.type}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{service.description}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{service.lastCheck}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Reports Tab */}
              {tab === 'reports' && (
                <div className="space-y-2">
                  {reports.map(report => (
                    <div
                      key={report.id}
                      className={`flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-secondary/30 transition-colors cursor-pointer ${
                        !report.enabled ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{report.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                            {report.frequency}
                          </span>
                          {!report.enabled && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                              paused
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          To: {report.recipients.join(', ')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Next: {report.nextSend}</p>
                        {report.lastSent && (
                          <p className="text-xs text-muted-foreground">Last: {report.lastSent}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
